import axios from 'axios';
import { MacroState } from './tradeSignalsService';

export type { MacroState };

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface StopAdjustment {
  date: string;
  old_stop: number;
  new_stop: number;
  trigger_price: number;
}

export interface StockSignalTechnicals {
  rsi_14: number | null;
  macd_histogram: number | null;
  sma_20: number | null;
  sma_50: number | null;
  bb_upper: number | null;
  bb_lower: number | null;
  bb_position: number | null;
  atr_14: number | null;
  volume_ratio: number | null;
  avwap_swing: number | null;
  avwap_swing_distance: number | null;
  avwap_20d: number | null;
  avwap_20d_distance: number | null;
}

export interface StockSignalSentiment {
  score: number;
  label: string;
  confidence?: number;
  catalyst?: string;
  headlines?: Array<{ title: string; publisher: string; time?: number }>;
  sources_count?: number;
}

export interface IntradayCandle {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface ScoreBreakdown {
  technical: number;
  volume: number;
  ivr: number;
  trend: number;
  market: number;
  intraday?: number;
  intraday_timing?: number;
}

export interface StockSignal {
  signal_id: string;
  date_score: string;
  symbol: string;
  strategy_id: string;
  strategy_name: string;
  direction: string;
  status: 'open' | 'closed';
  score: number;
  entry_price: number;
  target_price: number;
  stop_loss: number;
  trailing_stop: number;
  high_since_entry: number;
  stop_adjustments: StopAdjustment[];
  risk_reward_ratio: number;
  expected_hold_days: number;
  current_price: number;
  unrealized_pnl_pct: number;
  unrealized_pnl_dollars?: number;
  technicals: StockSignalTechnicals;
  sentiment?: StockSignalSentiment;
  market_context?: { spy_trend: string; spy_rsi: number };
  score_breakdown: ScoreBreakdown;
  rationale: string[];
  snapshot_hour?: string;
  opened_at: string;
  last_updated: string;
  timestamp: string;
  // Intraday data
  intraday_candles?: IntradayCandle[];
  // Closed fields
  close_price?: number;
  close_reason?: string;
  closed_at?: string;
  realized_pnl_pct?: number;
  hold_days?: number;
}

export interface StockSignalsResponse {
  openSignals: StockSignal[];
  closedSignals: StockSignal[];
  count: number;
  openCount: number;
  closedCount: number;
  weekStart: string;
  weekEnd: string;
  view: string;
  tier: string;
  macro?: MacroState | null;
}

export interface HistoricalStockSignalsResponse {
  signals: StockSignal[];
  signalsByWeek: { [weekKey: string]: StockSignal[] };
  count: number;
  dateRange: { start: string; end: string };
  view: string;
  tier: string;
}

export interface HistoricalStockSignalsOptions {
  startDate: string;
  endDate: string;
  limit?: number;
}

export const stockSignalsService = {
  async getSignals(token: string): Promise<StockSignalsResponse> {
    const response = await api.get('/stock-signals?includeIntraday=true', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getOpenSignals(token: string): Promise<{ signals: StockSignal[]; count: number }> {
    const response = await api.get('/stock-signals?view=open', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getHistoricalSignals(token: string, options: HistoricalStockSignalsOptions): Promise<HistoricalStockSignalsResponse> {
    const params = new URLSearchParams({
      view: 'history',
      startDate: options.startDate,
      endDate: options.endDate,
    });
    if (options.limit) {
      params.append('limit', options.limit.toString());
    }
    const response = await api.get(`/stock-signals?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getPreview(token: string, products: string = 'stock,spx,spy,es,mes'): Promise<{ previews: Record<string, any> }> {
    const response = await api.get(`/signals/preview?products=${products}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};
